/**
 * Error Categorization Utility
 *
 * Categorizes errors into types with specific recovery actions.
 * Similar to KDS's parseRegistrationError but adapted for Portal context.
 */

import { logger } from "~/lib/logger";

export type ErrorType =
  | "auth"
  | "permission"
  | "network"
  | "validation"
  | "not_found"
  | "rate_limit"
  | "server"
  | "generic";

export interface ParsedError {
  /** User-friendly error message */
  message: string;
  /** Error category for determining UI/recovery actions */
  type: ErrorType;
  /** Suggested action text for buttons */
  actionText?: string;
  /** Action to take (used by error boundary) */
  action?: "sign_in" | "retry" | "go_back" | "go_home" | "contact_support";
  /** Original error for logging */
  originalError?: unknown;
}

/**
 * Parse any error into a user-friendly categorized error
 */
export function parseError(error: unknown): ParsedError {
  const errorMessage = getErrorMessage(error);
  const lowerMessage = errorMessage.toLowerCase();

  // Authentication errors (expired token, not signed in)
  if (
    lowerMessage.includes("unauthenticated") ||
    lowerMessage.includes("not authenticated") ||
    lowerMessage.includes("token expired") ||
    lowerMessage.includes("invalid token") ||
    lowerMessage.includes("jwt expired") ||
    lowerMessage.includes("session expired") ||
    lowerMessage.includes("sign in")
  ) {
    return {
      message: "Your session has expired. Please sign in again.",
      type: "auth",
      actionText: "Sign In",
      action: "sign_in",
      originalError: error,
    };
  }

  // Permission/authorization errors
  if (
    lowerMessage.includes("unauthorized") ||
    lowerMessage.includes("forbidden") ||
    lowerMessage.includes("not a member") ||
    lowerMessage.includes("access denied") ||
    lowerMessage.includes("permission") ||
    lowerMessage.includes("not allowed")
  ) {
    return {
      message: "You don't have permission to access this resource.",
      type: "permission",
      actionText: "Go Back",
      action: "go_back",
      originalError: error,
    };
  }

  // Not found errors
  if (
    lowerMessage.includes("not found") ||
    lowerMessage.includes("does not exist") ||
    lowerMessage.includes("no such") ||
    lowerMessage.includes("404")
  ) {
    return {
      message: "The requested resource could not be found.",
      type: "not_found",
      actionText: "Go Home",
      action: "go_home",
      originalError: error,
    };
  }

  // Network/connection errors
  if (
    lowerMessage.includes("network") ||
    lowerMessage.includes("fetch") ||
    lowerMessage.includes("failed to fetch") ||
    lowerMessage.includes("network request failed") ||
    lowerMessage.includes("offline") ||
    lowerMessage.includes("timeout") ||
    lowerMessage.includes("econnrefused") ||
    lowerMessage.includes("connection") ||
    lowerMessage.includes("websocket")
  ) {
    return {
      message: "Connection lost. Please check your internet and try again.",
      type: "network",
      actionText: "Retry",
      action: "retry",
      originalError: error,
    };
  }

  // Rate limiting
  if (
    lowerMessage.includes("rate limit") ||
    lowerMessage.includes("too many requests") ||
    lowerMessage.includes("429")
  ) {
    return {
      message: "Too many requests. Please wait a moment and try again.",
      type: "rate_limit",
      actionText: "Retry",
      action: "retry",
      originalError: error,
    };
  }

  // Validation errors
  if (
    lowerMessage.includes("invalid") ||
    lowerMessage.includes("validation") ||
    lowerMessage.includes("required") ||
    lowerMessage.includes("must be") ||
    lowerMessage.includes("cannot be")
  ) {
    return {
      message: errorMessage || "Please check your input and try again.",
      type: "validation",
      actionText: "Go Back",
      action: "go_back",
      originalError: error,
    };
  }

  // Server errors
  if (
    lowerMessage.includes("internal server") ||
    lowerMessage.includes("500") ||
    lowerMessage.includes("502") ||
    lowerMessage.includes("503") ||
    lowerMessage.includes("server error")
  ) {
    return {
      message: "Something went wrong on our end. Please try again later.",
      type: "server",
      actionText: "Retry",
      action: "retry",
      originalError: error,
    };
  }

  // Generic fallback
  const parsed: ParsedError = {
    message: errorMessage || "An unexpected error occurred. Please try again.",
    type: "generic",
    actionText: "Try Again",
    action: "retry",
    originalError: error,
  };

  logger.warn("error_parsed_generic", {
    error_message: errorMessage,
    error_type: "generic",
  });

  return parsed;
}

/**
 * Extract error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    // Handle Convex errors
    if ("data" in error) {
      const data = error.data;
      if (typeof data === "string") {
        return data;
      }
    }
    if ("message" in error) {
      const message = error.message;
      if (typeof message === "string") {
        return message;
      }
    }
  }
  return "An unknown error occurred";
}

/**
 * Get appropriate icon name for error type (for use with lucide-react)
 */
export function getErrorIcon(type: ErrorType): string {
  switch (type) {
    case "auth": {
      return "LogIn";
    }
    case "permission": {
      return "ShieldX";
    }
    case "network": {
      return "WifiOff";
    }
    case "not_found": {
      return "FileQuestion";
    }
    case "rate_limit": {
      return "Clock";
    }
    case "validation": {
      return "AlertTriangle";
    }
    case "server": {
      return "ServerCrash";
    }
    default: {
      return "AlertCircle";
    }
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(type: ErrorType): boolean {
  return ["network", "rate_limit", "server", "generic"].includes(type);
}
